/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';

import { InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MAP_LOCALE_TO_DATETIME_FORMAT } from '../../constants';

import { getDailyReportsMinDate, getYesterdayDate } from '../../utils';
import { DateType } from '../../types/general.types';
import useSelectedLocale from '../../utils/useSelectedLocale';

interface Props {
  onStartDateChange: (newValue: Date | null) => void;
  onEndDateChange: (newValue: Date | null) => void;
  startDate: DateType;
  endDate: DateType;
}

export default function DateTimeIntervalFilter({
  onStartDateChange,
  onEndDateChange,
  startDate,
  endDate,
}: Props) {
  const { t } = useTranslation();
  const locale = useSelectedLocale();

  const getAdjustedMaxDate = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  };

  const getAdjustedMinDate = () => {
    const minDate = getDailyReportsMinDate();
    // if (minDate) {
    //   minDate.setHours(0, 0, 0, 0);
    // }
    return minDate;
  };

  const handleStartDateSelected = (newStartDate: Date | null) => {
    onStartDateChange(newStartDate); // Call the original handler to update parent's startDate state

    if (newStartDate) {
      // Create a new Date object for endDate based on newStartDate
      const newEndDate = new Date(newStartDate.getTime());
      // Add 30 minutes
      newEndDate.setMinutes(newEndDate.getMinutes() + 30);

      // Also ensure the newEndDate doesn't exceed the overall maxDate
      const overallMaxDate = getAdjustedMaxDate();
      if (overallMaxDate && newEndDate > overallMaxDate) {
        onEndDateChange(overallMaxDate); // Cap it at the overall maxDate
      } else {
        onEndDateChange(newEndDate); // Update parent's endDate state
      }
    } else {
      // If startDate is cleared, you might want to clear endDate too, or leave it.
      // For now, let's clear it if startDate is cleared.
      onEndDateChange(null);
    }
  };

  // For the End Date Picker, the minDate should be the startDate
  // to prevent selecting an end time before the start time.
  const minEndDate = startDate ? new Date(startDate.getTime()) : getAdjustedMinDate();


  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          mt: 1,
          mb: 1,
        }}
      >
        <InputLabel sx={{ paddingRight: 1, minWidth: 110 }}>{t('from')}</InputLabel>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <DateTimePicker
            inputFormat={MAP_LOCALE_TO_DATETIME_FORMAT[locale.code as keyof typeof MAP_LOCALE_TO_DATETIME_FORMAT]}
            value={startDate}
            maxDate={getAdjustedMaxDate()}
            minDate={getAdjustedMinDate()}
            onChange={handleStartDateSelected} // Use the new wrapper handler
            minutesStep={30}
            renderInput={(params: any) => (
              <TextField
                {...params}
                sx={{ width: 250 }}
                inputProps={{ ...(params.inputProps || {}), readOnly: true }}
              />
            )}
            onError={(reason, value) => console.error('DateTimePicker (start) error:', reason, value)}
          />
        </LocalizationProvider>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          mt: 1,
          mb: 1,
        }}
      >
        <InputLabel sx={{ paddingRight: 1, minWidth: 110 }}>{t('until')}</InputLabel>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <DateTimePicker
            inputFormat={MAP_LOCALE_TO_DATETIME_FORMAT[locale.code as keyof typeof MAP_LOCALE_TO_DATETIME_FORMAT]}
            value={endDate}
            maxDate={getAdjustedMaxDate()}
            // The minDate for the end picker should be at least the startDate
            minDate={minEndDate}
            shouldDisableTime={(timeValue, clockType) => {
              if (!startDate || !endDate) return false;
              
              // If the selected date is the same as start date
              const isSameDate = endDate.toDateString() === startDate.toDateString();
              if (!isSameDate) return false;
              
              // Disable times before the start time on the same date
              if (clockType === 'hours') {
                return timeValue < startDate.getHours();
              }
              if (clockType === 'minutes' && timeValue < startDate.getHours()) {
                return timeValue < startDate.getMinutes();
              }
              
              return false;
            }}
            onChange={onEndDateChange} // This remains the original handler
            minutesStep={30}
            renderInput={(params: any) => (
              <TextField
                {...params}
                sx={{ width: 250 }}
                inputProps={{ ...(params.inputProps || {}), readOnly: true }}
              />
            )}
            onError={(reason, value) => console.error('DateTimePicker (end) error:', reason, value)}
          />
        </LocalizationProvider>
      </Box>
    </>
  );
}